import { debug, logError } from '../../utils/logger';

/**
 * Facade for calling into the Attestation Service.
 */
export class AttestationFacade {
  private readonly baseUrl = 'proxy/attestationservice';
  private readonly submitEndpoint = '/submit-attestation';
  constructor(
    private api: {
      post: any;
    }
  ) {
    // NOTE: the following comments do not apply while we use a proxy in our sails api to hit the attestation service
    // Initialize the facade with parameters specific to the Attestation Service stack being used (once it is hit directly)
    // (e.g., prod, staging, individual developer).
  }

  /**
   * Calls to submit an attestation to the Attestation Service.
   * @param neighborUserId User ID of the currently logged in neighbor.
   *        Used for which neighbor the attestation belongs to as well as the principal/actor that
   *        is submitting the attestation (given that these can't be different on the neighbor app).
   * @param visitId Registered visit that the attestations is submitted to.
   * @param programId ID of the generalized state program that this is attesting to.
   * @param programVersion Version of the state program that these answers apply to.
   * @param attestationAnswers Answers that the neighbor provided to the attestation questions.
   * @param isAttestationComplete True iff the neighbor got through all of the questions before saving.
   * @param eligibilityStatus
   * @throws iff the attestation was not saved to the service.
   */
  public async submit(
    neighborUserId: number,
    visitId: number,
    programId: string,
    programVersion: string,
    attestationAnswers: unknown,
    isAttestationComplete: boolean,
    eligibilityStatus: string = 'unknown' //TODO #2967: calculate and pass real eligibility status #2967
  ): Promise<void> {
    // only log answers to neighbor device on staging to prevent PII leakage
    debug(
      'Trying to submit attestation service request: ' +
        JSON.stringify(
          {
            neighborUserId,
            visitId,
            programId,
            programVersion,
            attestationAnswers,
            isAttestationComplete,
            eligibilityStatus,
          },
          undefined,
          2
        )
    );

    try {
      const response = await this.api.post(`${this.baseUrl}${this.submitEndpoint}`, {
        neighborId: String(neighborUserId),
        visitId: String(visitId),
        programId,
        programVersion,
        eligibilityStatus,
        answers: attestationAnswers,
        actorType: 'neighbor',
        actorId: String(neighborUserId),
      });

      // Check response status or specific error codes
      if (response && response.status >= 400) {
        throw new Error(
          `Attestation service returned error status: ${response.status}, message: ${
            response.message || 'Unknown error'
          }`
        );
      }
    } catch (error) {
      // Add context to the error before re-throwing
      const contextError = new Error(
        `Failed to submit attestation for user ${neighborUserId}, visit ${visitId}, program ${programId}: ${
          error instanceof Error ? error.message : String(error)
        }`,
        { cause: error instanceof Error ? error : undefined }
      );

      // Log the error for debugging purposes
      debug(`Attestation submission error: ${contextError.message}`);

      // Re-throw the error with context
      throw contextError;
    }
  }
}
